import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faWineGlass, faFire, faTorah, faStar, faGift, faScroll,
  faChevronRight, faUsers, faChartLine, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { getExperienceType } from '../../data/experienceTypes';
import { calculateProgress, getExperienceStatus, EXPERIENCE_STATUS } from '../../utils/experiences';

// Map experience types to FontAwesome icons
export function getExperienceIcon(type) {
  const iconMap = {
    'passover-seder': faWineGlass,
    'shabbat': faFire,
    'hanukkah': faStar,
    'rosh-hashanah': faTorah,
    'yom-kippur': faTorah,
    'sukkot': faGift,
    'purim': faScroll,
    'bar-mitzvah': faTorah,
    'bat-mitzvah': faTorah,
    'shavuot': faTorah,
  };
  return iconMap[type] || faGift;
}

export default function ExperienceTimelineCard({ experience, type, index, mobile = false }) {
  const navigate = useNavigate();
  const expDate = experience.date?.toDate ? experience.date.toDate() : (experience.date ? new Date(experience.date) : null);
  const daysUntil = expDate ? Math.ceil((expDate - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const status = getExperienceStatus(experience);
  const isPast = status === EXPERIENCE_STATUS.COMPLETED;
  const progress = calculateProgress(experience);
  const experienceType = getExperienceType(experience.type);
  const IconComponent = getExperienceIcon(experience.type);

  return (
    <motion.div
      initial={{ opacity: 0, y: mobile ? 0 : 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`
        ${mobile ? '' : 'ml-20 relative'}
      `}
    >
      {/* Timeline dot (Desktop) */}
      {!mobile && (
        <div className="absolute -left-[3.25rem] top-6 w-6 h-6 rounded-full bg-primary border-4 border-background shadow-lg" />
      )}

      {/* Card */}
      <motion.div
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => navigate(`/experience/${experience.id}`)}
        className={`
          bg-white rounded-2xl border-2 transition-all cursor-pointer
          ${isPast 
            ? 'border-gray-200 opacity-75 hover:opacity-100' 
            : 'border-border hover:border-primary hover:shadow-xl'
          }
          p-5 md:p-6
        `}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="text-3xl md:text-4xl text-primary">
              <FontAwesomeIcon icon={IconComponent} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-serif font-bold text-xl md:text-2xl text-text-primary line-clamp-1">
                {experience.title}
              </h3>
              <p className="text-sm md:text-base text-text-secondary">
                {expDate ? format(expDate, 'EEEE, MMMM d, yyyy') : 'Date TBD'}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div className={`
            text-xs md:text-sm font-bold px-3 py-1.5 rounded-full whitespace-nowrap
            ${isPast
              ? 'bg-gray-100 text-text-secondary'
              : daysUntil <= 7
                ? 'bg-accent/20 text-accent-dark animate-pulse'
                : daysUntil <= 30
                  ? 'bg-primary/10 text-primary'
                  : 'bg-blue-50 text-blue-600'
            }
          `}>
            {isPast 
              ? (
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3" />
                  Completed
                </span>
              )
              : daysUntil === 0 
                ? 'Today!' 
                : daysUntil === 1 
                  ? 'Tomorrow'
                  : `${daysUntil} days`
            }
          </div>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
          {experience.guests && (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faUsers} className="w-4 h-4 text-text-secondary" />
              <span className="text-sm font-semibold text-text-primary">
                {experience.guests} guests
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faChartLine} className="w-4 h-4 text-text-secondary" />
            <span className="text-sm font-semibold text-text-primary">
              {experience.currentPhase || 'Planning'}
            </span>
          </div>
          {!isPast && progress !== undefined && (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-text-secondary" />
              <span className="text-sm font-semibold text-text-primary">
                {progress}% ready
              </span>
            </div>
          )}
        </div>

        {/* Progress Bar (Upcoming only) */}
        {!isPast && progress !== undefined && (
          <div className="mb-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-r from-primary to-primary-light h-2 rounded-full"
              />
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="flex justify-end">
          <span className="text-sm font-semibold text-primary group-hover:underline flex items-center gap-1">
            {isPast ? 'View Details' : 'Continue Planning'}
            <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

